export class ApiError extends Error {
  status: number;
  fieldErrors?: Record<string, string[]>;
  constructor(message: string, status: number, fieldErrors?: Record<string, string[]>) {
    super(message);
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

let csrfToken: string | null = null;

export function resetCsrfToken(): void {
  csrfToken = null;
}

async function ensureCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  const res = await fetch('/api/auth/csrf', { credentials: 'include' });
  const data = await res.json();
  csrfToken = data.csrfToken;
  return csrfToken!;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  isFormData?: boolean;
}

export async function apiRequest<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;

  if (options.body !== undefined) {
    if (options.isFormData) {
      body = options.body as FormData;
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(options.body);
    }
  }

  if (method !== 'GET') {
    headers['X-CSRF-Token'] = await ensureCsrfToken();
  }

  let res = await fetch(path, {
    method,
    credentials: 'include',
    headers,
    body,
  });

  // La sesion puede cambiar tras logout, reinicio del backend o en otra
  // pestaña; renueva el token una vez antes de mostrar un 403 de CSRF.
  if (res.status === 403 && method !== 'GET') {
    resetCsrfToken();
    headers['X-CSRF-Token'] = await ensureCsrfToken();
    res = await fetch(path, {
      method,
      credentials: 'include',
      headers,
      body,
    });
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  const data = contentType.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    const message = typeof data === 'object' && data && 'error' in data ? (data as { error: string }).error : 'Ocurrio un error inesperado.';
    const fieldErrors = typeof data === 'object' && data && 'fieldErrors' in data ? (data as { fieldErrors: Record<string, string[]> }).fieldErrors : undefined;
    throw new ApiError(message, res.status, fieldErrors);
  }

  return data as T;
}

export function apiDownloadUrl(path: string): string {
  return path;
}
